import { isProviderId, parseInstanceKey, type ProviderId, PROVIDERS } from './catalog'
import { SENSITIVITY_STEPS, type Workload } from './fit'
import { DEFAULT_OPTIONS, isProviderOption, type ProviderOptions } from './providers'
import { formatCpu, formatMemory, parseCpu, parseMemory } from './quantity'
import { type Interval, isInterval, isSortOrder, type SortOrder } from './ranking'

/**
 * Share links.
 *
 * Same contract as the other tools: the whole design lives in the URL fragment,
 * which browsers never send to a server. The payload is a versioned query string —
 * readable, and tolerant: a field that does not parse falls back to its default
 * instead of discarding the rest of the link.
 *
 *   #v=1&p=aws&req=250m,512Mi&lim=500m,1Gi&ds=100m,256Mi,3&a=aws:m7i.xlarge
 *
 * Fields left at their default are omitted, so a typical link stays short.
 */
const VERSION = '1'

export interface DesignState {
  /** The provider whose instances the list shows. */
  readonly provider: ProviderId
  readonly options: ProviderOptions
  readonly workload: Workload
  /** Selected families for `provider`; `null` means the catalogue's defaults. */
  readonly families: readonly string[] | null
  readonly sort: SortOrder
  readonly interval: Interval
  /** Instance keys (`aws:m7i.xlarge`) in the two comparison slots. */
  readonly primary: string | null
  readonly secondary: string | null
  /** Cost-sensitivity step, −9…9 tenths of the requests. */
  readonly step: number
}

export const DEFAULT_WORKLOAD: Workload = {
  request: { cpu: 250, memory: 512 },
  limit: { cpu: 500, memory: 1024 },
  daemonsets: { cpu: 0, memory: 0, pods: 0 },
}

export const DEFAULT_STATE: DesignState = {
  provider: 'aws',
  options: DEFAULT_OPTIONS,
  workload: DEFAULT_WORKLOAD,
  families: null,
  sort: 'cost-per-pod-asc',
  interval: 'month',
  primary: 'aws:m7i.xlarge',
  secondary: null,
  step: 0,
}

const limitText = (value: number | null, format: (n: number) => string): string =>
  value === null ? '-' : format(value)

export function encodeState(state: DesignState): string {
  const { workload } = state
  const params = new URLSearchParams({ v: VERSION, p: state.provider })
  params.set('req', `${formatCpu(workload.request.cpu)},${formatMemory(workload.request.memory)}`)
  params.set(
    'lim',
    `${limitText(workload.limit.cpu, formatCpu)},${limitText(workload.limit.memory, formatMemory)}`,
  )
  const ds = workload.daemonsets
  if (ds.cpu || ds.memory || ds.pods) {
    params.set('ds', `${formatCpu(ds.cpu)},${formatMemory(ds.memory)},${ds.pods}`)
  }
  if (state.primary) params.set('a', state.primary)
  if (state.secondary) params.set('b', state.secondary)
  const options = PROVIDERS.filter((p) => state.options[p] !== DEFAULT_OPTIONS[p]).map(
    (p) => `${p}.${state.options[p]}`,
  )
  if (options.length) params.set('o', options.join(','))
  if (state.families) params.set('f', state.families.join(','))
  if (state.sort !== DEFAULT_STATE.sort) params.set('s', state.sort)
  if (state.interval !== DEFAULT_STATE.interval) params.set('t', state.interval)
  if (state.step !== 0) params.set('e', String(state.step))
  // Commas and colons are legal in a fragment; leaving them unescaped keeps links legible.
  return params.toString().replace(/%2C/g, ',').replace(/%3A/g, ':')
}

const nonNegative = (value: number | null): value is number =>
  value !== null && Number.isFinite(value) && value >= 0

function decodeResources(text: string | null): { cpu: number; memory: number } | null {
  const [cpuText, memoryText] = (text ?? '').split(',')
  const cpu = parseCpu(cpuText ?? '')
  const memory = parseMemory(memoryText ?? '')
  return nonNegative(cpu) && nonNegative(memory) ? { cpu, memory } : null
}

function decodeLimits(text: string | null): Workload['limit'] | null {
  const [cpuText = '', memoryText = ''] = (text ?? '').split(',')
  const cpu = cpuText === '-' ? null : parseCpu(cpuText)
  const memory = memoryText === '-' ? null : parseMemory(memoryText)
  const ok = (value: number | null, raw: string): boolean => raw === '-' || nonNegative(value)
  return text !== null && ok(cpu, cpuText) && ok(memory, memoryText) ? { cpu, memory } : null
}

function decodeDaemonsets(text: string | null): Workload['daemonsets'] | null {
  const resources = decodeResources(text)
  const pods = Number((text ?? '').split(',')[2] ?? '0')
  return resources && Number.isInteger(pods) && pods >= 0 ? { ...resources, pods } : null
}

function decodeOptions(text: string | null): ProviderOptions {
  const options: Record<ProviderId, string> = { ...DEFAULT_OPTIONS }
  for (const pair of (text ?? '').split(',')) {
    // `provider.option`, split at the first dot so an option may contain one later.
    const dot = pair.indexOf('.')
    const provider = pair.slice(0, dot)
    const option = pair.slice(dot + 1)
    if (dot > 0 && isProviderId(provider) && isProviderOption(provider, option)) {
      options[provider] = option
    }
  }
  return options as ProviderOptions
}

const decodeKey = (text: string | null): string | null =>
  text !== null && parseInstanceKey(text) ? text : null

/** Returns `null` only for a fragment that is not one of ours; bad fields fall back. */
export function decodeState(payload: string): DesignState | null {
  const params = new URLSearchParams(payload.replace(/^#/, ''))
  if (params.get('v') !== VERSION) return null

  const provider = params.get('p')
  const sort = params.get('s')
  const interval = params.get('t')
  const step = Number(params.get('e') ?? '0')
  const families = params.get('f')
  return {
    provider: isProviderId(provider) ? provider : DEFAULT_STATE.provider,
    options: decodeOptions(params.get('o')),
    workload: {
      request: decodeResources(params.get('req')) ?? DEFAULT_WORKLOAD.request,
      limit: decodeLimits(params.get('lim')) ?? DEFAULT_WORKLOAD.limit,
      daemonsets: decodeDaemonsets(params.get('ds')) ?? DEFAULT_WORKLOAD.daemonsets,
    },
    families: families === null ? null : families.split(',').filter(Boolean),
    sort: isSortOrder(sort) ? sort : DEFAULT_STATE.sort,
    interval: isInterval(interval) ? interval : DEFAULT_STATE.interval,
    primary: decodeKey(params.get('a')),
    secondary: decodeKey(params.get('b')),
    step: SENSITIVITY_STEPS.includes(step) ? step : 0,
  }
}

/** Writes the state into the fragment without adding a history entry per keystroke. */
export function replaceLocationState(state: DesignState): void {
  if (typeof window === 'undefined') return
  const next = `${window.location.pathname}${window.location.search}#${encodeState(state)}`
  window.history.replaceState(null, '', next)
}

export function readLocationState(): DesignState | null {
  if (typeof window === 'undefined') return null
  return decodeState(window.location.hash)
}
